/**
 * The extracted style definition is prose with `{{var}}` placeholders naming palette colours (Art Manager
 * 2.0's format). These pure helpers split it into text and colour references so the page can show each
 * reference as a small swatch chip, and reveal it progressively.
 */
import type { ArtStyleSwatch } from './art-style-api';

export type DefinitionSegment = { kind: 'text'; text: string } | { kind: 'var'; name: string; swatch?: ArtStyleSwatch };

const VAR_RE = /\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g;

/** Only a plain hex colour is ever painted (the value comes from the service). */
export const safeHex = (hex: string): string | undefined =>
  /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(hex.trim()) ? hex.trim() : undefined;

/** Split the prose into text runs and colour references (resolved against the palette by name). */
export const parseDefinition = (text: string, palette: readonly ArtStyleSwatch[]): DefinitionSegment[] => {
  const byName = new Map(palette.map((swatch) => [swatch.name.toLowerCase(), swatch]));
  const out: DefinitionSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(VAR_RE)) {
    const at = match.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: text.slice(last, at) });
    const name = match[1];
    out.push({ kind: 'var', name, swatch: byName.get(name.toLowerCase()) });
    last = at + match[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
};

/** How many reveal units the segments hold: every character of text, and one per colour reference. */
export const revealUnits = (segments: readonly DefinitionSegment[]): number =>
  segments.reduce((sum, segment) => sum + (segment.kind === 'text' ? segment.text.length : 1), 0);

/** The segments cut to the first `units` reveal units (a colour reference shows whole or not at all). */
export const revealSegments = (segments: readonly DefinitionSegment[], units: number): DefinitionSegment[] => {
  const out: DefinitionSegment[] = [];
  let left = units;
  for (const segment of segments) {
    if (left <= 0) break;
    if (segment.kind === 'var') {
      out.push(segment);
      left -= 1;
      continue;
    }
    out.push(segment.text.length <= left ? segment : { kind: 'text', text: segment.text.slice(0, left) });
    left -= segment.text.length;
  }
  return out;
};
